import { useState } from 'react'
import { Modal } from './Modal'
import { DAY_NAMES, type Note } from '@shared/types'

interface Props {
  /** null for the week as a whole, 1-5 for a single day. */
  dayOfWeek: number | null
  title: string
  notes: Note[]
  currentUser: string
  /** Everyone in the household, so the prompt can name who a note is for. */
  people: string[]
  onAdd: (body: string) => Promise<void>
  onEdit: (noteId: string, body: string) => Promise<void>
  onDelete: (noteId: string) => Promise<void>
  onClose: () => void
}

/**
 * Who a week note is addressed to. The one other person in the household when
 * there is exactly one -- that is the common case and reads as a real
 * sentence -- and a neutral phrase otherwise. Never a name compiled into the
 * app: this text once said "Melanie" to every family.
 */
function otherParent(people: string[], me: string): string {
  const others = people.filter((p) => p.trim().toLowerCase() !== me.trim().toLowerCase())
  return others.length === 1 ? others[0]! : 'the rest of the family'
}

/** "14 Sep, 3:42pm" — enough to order a conversation without being noisy. */
function when(iso: string): string {
  // SQLite hands back "YYYY-MM-DD HH:MM:SS" in UTC with no zone marker.
  const d = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function NotesPanel({
  dayOfWeek,
  title,
  notes,
  currentUser,
  people,
  onAdd,
  onEdit,
  onDelete,
  onClose,
}: Props) {
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<{ id: string; body: string } | null>(null)

  const mine = notes.filter((n) => n.dayOfWeek === dayOfWeek)

  async function run(fn: () => Promise<void>) {
    if (busy) return
    setBusy(true)
    try {
      await fn()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <span className="muted" style={{ fontSize: 12.5 }}>
            {mine.length === 0 ? 'No notes yet' : `${mine.length} note${mine.length === 1 ? '' : 's'}`}
          </span>
          <div className="spacer" />
          <button className="btn ghost" onClick={onClose}>
            Done
          </button>
        </>
      }
    >
      {mine.length > 0 && (
        <div className="note-list">
          {mine.map((note) => (
            <article className="note-card" key={note.id}>
              <header className="note-head">
                <strong>{note.author || 'Someone'}</strong>
                <span className="muted">{when(note.createdAt)}</span>
                {note.updatedAt !== note.createdAt && <span className="muted">· edited</span>}
                <div className="spacer" />
                <button
                  className="btn ghost sm"
                  onClick={() => setEditing({ id: note.id, body: note.body })}
                  disabled={busy}
                >
                  Edit
                </button>
                <button
                  className="btn ghost sm danger"
                  onClick={() => {
                    if (confirm('Delete this note?')) void run(() => onDelete(note.id))
                  }}
                  disabled={busy}
                >
                  Delete
                </button>
              </header>

              {editing?.id === note.id ? (
                <div className="stack" style={{ gap: 8 }}>
                  <textarea
                    className="note-input"
                    value={editing.body}
                    autoFocus
                    onChange={(e) => setEditing({ id: note.id, body: e.target.value })}
                  />
                  <div className="row">
                    <button className="btn sm ghost" onClick={() => setEditing(null)} disabled={busy}>
                      Cancel
                    </button>
                    <div className="spacer" />
                    <button
                      className="btn sm primary"
                      disabled={busy || !editing.body.trim()}
                      onClick={() =>
                        void run(async () => {
                          await onEdit(note.id, editing.body.trim())
                          setEditing(null)
                        })
                      }
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <p className="note-body">{note.body}</p>
              )}
            </article>
          ))}
        </div>
      )}

      <div className="field">
        <label htmlFor="note-draft">
          {dayOfWeek === null ? 'Add a note about the week' : 'Add a note'}
        </label>
        <textarea
          id="note-draft"
          className="note-input"
          value={draft}
          placeholder={
            dayOfWeek === null
              ? `Anything ${otherParent(people, currentUser)} should know about this week…`
              : `Why this lunch, or how it went…`
          }
          onChange={(e) => setDraft(e.target.value)}
        />
        <div className="row">
          <span className="muted" style={{ fontSize: 12 }}>
            Posted as {currentUser}
          </span>
          <div className="spacer" />
          <button
            className="btn primary sm"
            disabled={busy || !draft.trim()}
            onClick={() =>
              void run(async () => {
                await onAdd(draft.trim())
                setDraft('')
              })
            }
          >
            {busy ? 'Saving…' : 'Add note'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

/** Label used by the day and week note buttons. */
export function noteLabel(count: number): string {
  return count === 0 ? 'Notes' : `Notes ${count}`
}

export const dayTitle = (dayOfWeek: number, date: string) =>
  `${DAY_NAMES[dayOfWeek - 1]}, ${date} — notes`
